<div class="linked-devices-page">
    <div class="mb-4">
        <h2 class="text-2xl font-bold mb-2">Linked Devices</h2>
        <p class="text-color-secondary">Devices discovered and allocated on the main control system</p>
    </div>

    @if (loading()) {
    <div class="flex flex-col gap-3">
        <p-skeleton height="100px"></p-skeleton>
        <p-skeleton height="400px"></p-skeleton>
    </div>
    } @else if (error()) {
    <p-message severity="error">{{ error()! }}</p-message>
    } @else if (linkedDevices().length === 0) {
    <app-section-card>
        <app-empty-state icon="pi pi-inbox" title="No Linked Devices" description="This device has no linked devices connected or allocated." />
    </app-section-card>
    } @else {
    <!-- Summary Cards -->
    <div class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 mb-4">
        <div class="summary-card">
            <i class="pi pi-list text-2xl text-primary"></i>
            <div>
                <div class="text-2xl font-bold">{{ linkedDevices().length }}</div>
                <div class="text-sm text-color-secondary">Total Devices</div>
            </div>
        </div>
        <div class="summary-card">
            <i class="pi pi-check-circle text-2xl text-status-online"></i>
            <div>
                <div class="text-2xl font-bold text-status-online">{{ getOnlineCount() }}</div>
                <div class="text-sm text-color-secondary">Online</div>
            </div>
        </div>
        <button type="button" class="summary-card summary-card--action" [class.summary-card--active]="statusFilter() === 'attention'" [attr.aria-pressed]="statusFilter() === 'attention'" (click)="statusFilter.set(statusFilter() === 'attention' ? 'all' : 'attention')">
            <i class="pi pi-exclamation-triangle text-2xl text-status-warning"></i>
            <div>
                <div class="text-2xl font-bold text-status-warning">{{ getAttentionCount() }}</div>
                <div class="text-sm text-color-secondary">Needs attention</div>
            </div>
        </button>
        <div class="summary-card">
            <i class="pi pi-times-circle text-2xl text-status-offline"></i>
            <div>
                <div class="text-2xl font-bold text-status-offline">{{ getOfflineCount() }}</div>
                <div class="text-sm text-color-secondary">Offline</div>
            </div>
        </div>
        <div class="summary-card">
            <i class="pi pi-question-circle text-2xl" style="color: var(--text-color-secondary)"></i>
            <div>
                <div class="text-2xl font-bold" style="color: var(--text-color-secondary)">{{ getMissingCount() }}</div>
                <div class="text-sm text-color-secondary">Missing</div>
            </div>
        </div>
    </div>

    <!-- Search / Filter Bar -->
    <div class="flex flex-col md:flex-row items-start md:items-center gap-3 mb-4">
        <p-iconfield class="flex-1 w-full md:w-auto">
            <p-inputicon class="pi pi-search"></p-inputicon>
            <input type="text" pInputText placeholder="Search by name, type, manufacturer, model, serial..." class="w-full" [ngModel]="searchQuery()" (ngModelChange)="searchQuery.set($event)" />
        </p-iconfield>

        <p-select [options]="statusFilterOptions" [ngModel]="statusFilter()" (ngModelChange)="statusFilter.set($event)" optionLabel="label" optionValue="value" placeholder="Status" class="w-full md:w-auto" [style]="{ minWidth: '140px' }"></p-select>

        <p-selectbutton [options]="viewModeOptions" [ngModel]="viewMode()" (ngModelChange)="viewMode.set($event)" optionLabel="label" optionValue="value">
            <ng-template #item let-item>
                <i [class]="item.icon"></i>
            </ng-template>
        </p-selectbutton>
    </div>

    <!-- Results count -->
    @if (filteredDevices().length !== linkedDevices().length) {
    <div class="text-sm text-color-secondary mb-3">
        Showing {{ filteredDevices().length }} of {{ linkedDevices().length }} devices
    </div>
    }

    <!-- Card View -->
    @if (viewMode() === 'card') {
    @if (filteredDevices().length === 0) {
    <div class="text-center p-6 text-color-secondary">
        <i class="pi pi-filter-slash text-4xl mb-3"></i>
        <p>No devices match the current filters.</p>
    </div>
    } @else {
    <div class="device-card-grid">
        @for (device of filteredDevices(); track device.id) {
        <div class="device-card" (click)="navigateToDetail(device)" (contextmenu)="showContextMenu($event, device, linkedDeviceMenu)">
            <!-- Header: Status + Name -->
            <div class="device-card-header">
                <div class="flex items-center gap-2 min-w-0">
                    <span class="device-name">{{ displayName(device) }}</span>
                </div>
                <app-status-badge class="flex-shrink-0" [status]="linkedStatus(device)" size="sm" />
            </div>

            @if (linkedProblem(device); as problem) {
            <div class="device-problem" [title]="problem"><i class="pi pi-exclamation-triangle" aria-hidden="true"></i><span>{{ problem }}</span></div>
            }

            <!-- Subtitle: Type + Manufacturer/Model -->
            @if (getDeviceSubtitle(device)) {
            <div class="text-sm text-color-secondary mb-2">{{ getDeviceSubtitle(device) }}</div>
            }

            <!-- Details -->
            <div class="device-card-details">
                @if (device.serialNumber) {
                <div class="detail-row">
                    <span class="detail-label">Serial</span>
                    <span class="detail-value font-mono text-xs">{{ device.serialNumber }}</span>
                </div>
                }
                @if (device.versionInfo) {
                <div class="detail-row">
                    <span class="detail-label">Firmware</span>
                    <span class="detail-value text-xs">{{ device.versionInfo }}</span>
                </div>
                }
                @if (device.connectionInfo) {
                <div class="detail-row">
                    <span class="detail-label">Connection</span>
                    <span class="detail-value text-xs">{{ device.connectionInfo }}</span>
                </div>
                }
                @if (device.locationNodeId || device.allocatedRoomId) {
                <div class="detail-row">
                    <span class="detail-label">Room</span>
                    <span class="detail-value text-xs">
                        @if (device.locationNodeId) {
                        <a [routerLink]="['/location', device.locationNodeId]" class="text-primary hover:underline" (click)="$event.stopPropagation()">{{ locationLabel(device) }}</a>
                        } @else {
                        {{ locationLabel(device) }}
                        }
                    </span>
                </div>
                }
                <div class="detail-row">
                    <span class="detail-label">Last Seen</span>
                    <span class="detail-value text-xs">{{ formatLastSeen(device) }}</span>
                </div>
            </div>

            <!-- Capability Icons -->
            @if (hasAnyCapability(device)) {
            <div class="capability-row">
                @for (cap of getDeviceCapabilities(device); track cap.key) {
                <span class="capability-icon" [pTooltip]="cap.label" tooltipPosition="top">
                    <i [class]="cap.icon"></i>
                </span>
                }
            </div>
            }

            <!-- State Info (only for online devices with state data) -->
            @if (device.online && !device.missing && device.state) {
            <div class="device-card-state">
                @if (device.capabilities?.hasPower && device.state.powerStatus) {
                <span class="state-indicator" [class.on]="device.state.powerStatus === 'On'" [class.off]="device.state.powerStatus !== 'On'">
                    <i class="pi pi-power-off"></i> {{ getPowerStatusLabel(device) }}
                </span>
                }
                @if (device.capabilities?.hasInputControl && device.state.currentInput) {
                <span class="state-indicator">
                    <i class="pi pi-desktop"></i> {{ device.state.currentInput }}
                </span>
                }
                @if (device.capabilities?.hasStandby && device.state.inStandby) {
                <span class="state-indicator standby">
                    <i class="pi pi-moon"></i> Standby
                </span>
                }
            </div>
            }
        </div>
        }
    </div>
    }
    }

    <!-- Table View -->
    @if (viewMode() === 'table') {
    <app-section-card [flush]="true">
        <p-table
            [value]="filteredDevices()"
            dataKey="id"
            class="p-datatable-sm"
            [rowHover]="true"
            [paginator]="filteredDevices().length > 15"
            [rows]="15"
            [showCurrentPageReport]="true"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} devices">
            <ng-template #header>
                <tr>
                    <th style="width: 5rem">Status</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Manufacturer</th>
                    <th>Model</th>
                    <th>Serial Number</th>
                    <th>Firmware</th>
                    <th>Connection</th>
                    <th>Room</th>
                    <th>Capabilities</th>
                    <th>Last Seen</th>
                </tr>
            </ng-template>
            <ng-template #body let-device>
                <tr (click)="navigateToDetail(device)" (contextmenu)="showContextMenu($event, device, linkedDeviceMenu)" style="cursor: pointer">
                    <td>
                        <app-status-badge [status]="linkedStatus(device)" size="sm" />
                    </td>
                    <td>
                        <div class="font-semibold">{{ displayName(device) }}</div>
                        @if (linkedProblem(device); as problem) {
                        <div class="device-problem" [title]="problem"><span>{{ problem }}</span></div>
                        }
                    </td>
                    <td>{{ device.type || '-' }}</td>
                    <td>{{ device.manufacturerName || '-' }}</td>
                    <td>{{ device.modelName || '-' }}</td>
                    <td>
                        @if (device.serialNumber) {
                        <span class="font-mono text-xs">{{ device.serialNumber }}</span>
                        } @else { - }
                    </td>
                    <td>{{ device.versionInfo || '-' }}</td>
                    <td>{{ device.connectionInfo || '-' }}</td>
                    <td>
                        @if (device.locationNodeId) {
                        <a [routerLink]="['/location', device.locationNodeId]" class="text-primary hover:underline" (click)="$event.stopPropagation()">{{ locationLabel(device) }}</a>
                        } @else if (device.allocatedRoomId) {
                        <span class="text-xs text-color-secondary">{{ locationLabel(device) }}</span>
                        } @else { - }
                    </td>
                    <td>
                        <div class="flex gap-1">
                            @for (cap of getDeviceCapabilities(device); track cap.key) {
                            <i [class]="cap.icon" class="text-xs text-color-secondary" [pTooltip]="cap.label" tooltipPosition="top"></i>
                            }
                            @if (!hasAnyCapability(device)) { - }
                        </div>
                    </td>
                    <td>{{ formatLastSeen(device) }}</td>
                </tr>
            </ng-template>
            <ng-template #emptymessage>
                <tr>
                    <td colspan="11" class="text-center p-4 text-color-secondary">No devices match the current filters.</td>
                </tr>
            </ng-template>
        </p-table>
    </app-section-card>
    }
    }
</div>

<!-- Context Menu for Linked Devices -->
<p-contextmenu #linkedDeviceMenu [model]="contextMenuItems"></p-contextmenu>

<!-- Move Linked Device Dialog -->
<p-dialog header="Move Linked Device" [(visible)]="showMoveDialog" [modal]="true" [closable]="false" [style]="{ width: '40rem', maxHeight: '80vh' }">
    @if (selectedLinkedDevice(); as child) {
    <div class="flex flex-col gap-4">
        <div class="p-3 rounded device-info-card">
            <div class="text-sm text-color-secondary mb-1">Device</div>
            <div class="font-semibold">{{ displayName(child) }}</div>
            <div class="text-xs text-color-secondary font-mono">{{ child.uniqueId }}</div>
        </div>

        <p class="text-xs text-color-secondary m-0">Select a new location from the tree below.</p>

        @if (child.locationNodeId) {
        <div class="text-xs text-color-secondary">Current location is highlighted below</div>
        }

        <app-location-picker
            [locations]="availableLocations()"
            [namespaceId]="deviceSig()?.appNamespaceId ?? null"
            [currentLocationId]="child.locationNodeId ?? null"
            [disabled]="movingLinkedDeviceId() === child.id"
            [minHeight]="'300px'"
            [maxHeight]="'400px'"
            (locationSelected)="selectedTargetLocation.set($event)"
        />

        <!-- Selected location confirmation -->
        @if (selectedTargetLocation(); as targetId) {
        <div class="p-3 rounded flex items-center gap-2 selected-location-card">
            <i class="pi pi-check-circle text-primary"></i>
            <div>
                <div class="text-xs text-color-secondary">Selected location:</div>
                <div class="font-semibold">{{ getLocationName(targetId) }}</div>
            </div>
        </div>
        } @if (moveError()) {
        <p-message severity="error">{{ moveError()! }}</p-message>
        }
    </div>
    }
    <ng-template #footer>
        <div class="flex justify-end gap-2">
            <p-button label="Cancel" severity="secondary" (onClick)="showMoveDialog.set(false)" [disabled]="movingLinkedDeviceId() !== null"></p-button>
            <p-button label="Move" (onClick)="moveLinkedDevice()" [loading]="movingLinkedDeviceId() !== null" [disabled]="!selectedTargetLocation()"></p-button>
        </div>
    </ng-template>
</p-dialog>

<!-- Remove Linked Device Dialog -->
<p-dialog header="Remove Linked Device" [visible]="showRemoveDialog()" (onHide)="showRemoveDialog.set(false)" [modal]="true" [closable]="false" [style]="{ width: '440px' }">
    @if (removeTarget(); as dev) {
    <div class="flex flex-col gap-3">
        <p class="m-0 text-sm">Are you sure you want to remove <strong>{{ displayName(dev) }}</strong> from the linked devices list?</p>
        <p class="m-0 text-xs text-color-secondary">This device is no longer being reported by the parent processor. It will reappear automatically if it comes back online.</p>
        @if (removeError()) {
        <div class="text-xs text-status-offline">{{ removeError() }}</div>
        }
    </div>
    }
    <ng-template #footer>
        <div class="flex justify-end gap-2">
            <p-button label="Cancel" severity="secondary" (onClick)="showRemoveDialog.set(false)" [disabled]="removeSubmitting()"></p-button>
            <p-button label="Remove" severity="danger" icon="pi pi-trash" (onClick)="confirmRemoveLinkedDevice()" [loading]="removeSubmitting()"></p-button>
        </div>
    </ng-template>
</p-dialog>
